/**
 * General utilities.
 * @module api/util
 * @private
 */

/**
 * Matches URI Template expressions.
 * See https://datatracker.ietf.org/doc/html/rfc6570
 */
const urlTemplateRe = /\{[^\{\}]+\}/g;

/**
 * @param {string} href A URL.  This can be a URL template with {x} expressions.
 * @param {Object} params An object with search parameters to be added to the URL.
 * @return {string} The URL with search parameters added.
 */
export function addQueryParams(href, params) {
  const templateMatch = href.match(urlTemplateRe);
  const url = new URL(href);
  const searchParams = url.searchParams;
  for (const key in params) {
    searchParams.set(key, params[key]);
  }
  let updated = url.toString();
  if (templateMatch) {
    // The URL constructor encodes template expressions {foo} as %7Bfoo%7D.
    // This replaces the encoded expressions with the originals so the template can be evaluated.
    for (const expression of templateMatch) {
      updated = updated.replace(encodeURIComponent(expression), expression);
    }
  }
  return updated;
}

/**
 * Simplified polyfill for ES6 Object.assign.
 * @param {Object} target The target object.
 * @param {Object} src The source object(s).
 * @return {Object} The target object with source properties assigned.
 * @private
 */
export const assign = (...args) => {
  const target = args[0];
  for (let i = 1, ii = args.length; i < ii; ++i) {
    const src = args[i];
    for (const key in src) {
      target[key] = src[key];
    }
  }
  return target;
};

/**
 * Get the current location. More readily mocked than using the global
 * directly.
 *
 * @return {Location} The current location.
 */
export const currentLocation = () => {
  /* location is tricky to mock in the browser */
  /* istanbul ignore if */
  if (typeof location !== 'undefined') {
    return location;
  } else {
    return undefined;
  }
};
